// «Avisos y sonido» (04-11, Pau: «no me llega ninguna notificación. Yo quiero poder elegir el sonido y tal»): una hoja
// pequeña que se abre desde la campana de la cabecera de la ventana «Santi». Tres cosas:
//  1. el sonido de los avisos de Santi con la oficina abierta (sonidos.ts): cinco, cada uno con «Probar»; elegir uno
//     también lo toca; se recuerda en este aparato;
//  2. los avisos del sistema en este aparato (lo de ActivarAvisos, aquí siempre a la vista: activos, «Activar»,
//     bloqueados, instalar en el iPad…);
//  3. una línea llana: el aviso del sistema suena con el sonido del sistema (no se elige desde la web) y, si no llega,
//     dónde se permite.
// 04-11 (Pau: «suenan muy flojito»): y el «Volumen» (0-100 %, 80 % por defecto, por aparato), que vale para los avisos
// y para «Probar»; al moverlo, suena el sonido elegido una vez al volumen nuevo (cuando para de moverlo). 0 % = no suena
// (el sonido elegido se queda).
import { Check, Play, Volume1, Volume2, VolumeX } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { useHablaComoPau } from '../pelotas/store';
import { Boton, Ventana } from '../ui';
import { ICONO, sistemaActual, textoDe, useAvisosAparato, type Aparato, type EstadoAvisos, type Sistema } from './ActivarAvisos';
import { SONIDOS, tocarSonido, useSonidoElegido, useVolumenElegido, type SonidoAviso } from './sonidos';

const GRIS = 'text-oficina-texto2 hover:text-oficina-texto';
/** Al mover el volumen, cuánto se espera a que pare antes de tocar el sonido de prueba (uno, no uno por paso). */
export const ESPERA_PRUEBA_VOLUMEN_MS = 250;

export function HojaAvisos({ abierta, alCerrar }: { abierta: boolean; alCerrar: () => void }) {
  const comoPau = useHablaComoPau();
  const [sonido, elegir] = useSonidoElegido();
  const [volumen, elegirVolumen] = useVolumenElegido();
  const avisos = useAvisosAparato(abierta && comoPau, 'hoja');
  const sonidoAhora = useRef(sonido);
  useEffect(() => {
    sonidoAhora.current = sonido;
  }, [sonido]);
  const prueba = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (prueba.current) clearTimeout(prueba.current);
    };
  }, []);
  return (
    <Ventana abierta={abierta} alCerrar={alCerrar} titulo={t('oficinaV2.avisosAparato.avisosYSonido')} descripcion={t('oficinaV2.avisosAparato.hoja.descripcion')} ancho="sm">
      <ContenidoHojaAvisos
        sonido={sonido}
        alElegir={(id) => {
          elegir(id);
          void tocarSonido(id);
        }}
        alProbar={(id) => void tocarSonido(id)}
        volumen={volumen}
        alVolumen={(n) => {
          elegirVolumen(n);
          if (prueba.current) clearTimeout(prueba.current);
          prueba.current = setTimeout(() => void tocarSonido(sonidoAhora.current, n), ESPERA_PRUEBA_VOLUMEN_MS);
        }}
        estado={avisos.estado}
        aparato={avisos.aparato}
        sistema={sistemaActual()}
        alActivar={() => void avisos.activar()}
      />
    </Ventana>
  );
}

/** Lo de dentro, sin estado propio (lo pinta la hoja y, fijo, la historia de Storybook). */
export function ContenidoHojaAvisos({
  sonido,
  alElegir,
  alProbar,
  volumen,
  alVolumen,
  estado,
  aparato,
  sistema,
  alActivar,
}: {
  sonido: SonidoAviso;
  alElegir: (id: SonidoAviso) => void;
  alProbar: (id: SonidoAviso) => void;
  /** 0-100. */
  volumen: number;
  alVolumen: (n: number) => void;
  estado: EstadoAvisos;
  aparato: Aparato;
  sistema: Sistema;
  alActivar: () => void;
}) {
  const nombreAparato = t(`oficinaV2.avisosAparato.aparato.${aparato}`);
  const Icono = ICONO[estado];
  const conAccion = estado === 'pedir' || estado === 'activando' || estado === 'fallo';
  return (
    <div className="grid gap-5 pb-1" data-testid="hoja-avisos">
      <section>
        <h3 className="mb-1.5 text-o-sub font-semibold text-oficina-texto2">{t('oficinaV2.avisosAparato.sonido.titulo')}</h3>
        <div role="radiogroup" aria-label={t('oficinaV2.avisosAparato.sonido.titulo')} className="overflow-hidden rounded-o-m bg-oficina-control/60">
          {SONIDOS.map(({ id }, i) => {
            const elegido = id === sonido;
            return (
              <div key={id} className={cn('flex items-center gap-1 pr-1.5', i > 0 && 'border-t border-white/[0.06]')} data-testid="hoja-sonido" data-sonido={id}>
                <button
                  type="button"
                  role="radio"
                  aria-checked={elegido}
                  onClick={() => alElegir(id)}
                  className="flex min-h-11 min-w-0 flex-1 items-center gap-3 px-3.5 text-left text-o-cuerpo text-oficina-texto outline-none transition-colors duration-150 ease-o-apple hover:bg-white/[0.04] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-oficina-enlace/70"
                >
                  <span className="flex w-4 shrink-0 justify-center" aria-hidden>
                    {elegido ? <Check className="h-4 w-4" strokeWidth={2.75} /> : null}
                  </span>
                  <span className={cn('min-w-0 flex-1 truncate', elegido && 'font-semibold')}>{t(`oficinaV2.avisosAparato.sonido.nombres.${id}`)}</span>
                </button>
                {id === 'ninguno' ? null : (
                  <Boton variante="fantasma" tamano="sm" icono={Play} onClick={() => alProbar(id)} className={GRIS} data-testid="hoja-probar">
                    {t('oficinaV2.avisosAparato.sonido.probar')}
                  </Boton>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <section>
        <h3 className="mb-1.5 text-o-sub font-semibold text-oficina-texto2">{t('oficinaV2.avisosAparato.sonido.volumen')}</h3>
        {/* El control nativo (teclado, lector de pantalla, el dedo en el iPad) con el blanco de la oficina; 44 de alto. */}
        <div className="flex items-center gap-3 rounded-o-m bg-oficina-control/60 px-3.5" data-testid="hoja-volumen">
          {volumen === 0 ? <VolumeX className="h-[18px] w-[18px] shrink-0 text-oficina-texto2" aria-hidden /> : <Volume1 className="h-[18px] w-[18px] shrink-0 text-oficina-texto2" aria-hidden />}
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={volumen}
            onChange={(e) => alVolumen(Number(e.target.value))}
            aria-label={t('oficinaV2.avisosAparato.sonido.volumenEtiqueta')}
            aria-valuetext={t('oficinaV2.avisosAparato.sonido.porcentaje', { n: volumen })}
            className="h-11 min-w-0 flex-1 cursor-pointer rounded-o-s accent-oficina-texto outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace/70"
            data-testid="hoja-volumen-control"
          />
          <Volume2 className="h-[18px] w-[18px] shrink-0 text-oficina-texto2" aria-hidden />
          <span className="w-11 shrink-0 text-right text-o-sub tabular-nums text-oficina-texto2" data-testid="hoja-volumen-valor">
            {t('oficinaV2.avisosAparato.sonido.porcentaje', { n: volumen })}
          </span>
        </div>
      </section>

      <section>
        <h3 className="mb-1.5 text-o-sub font-semibold text-oficina-texto2">{t('oficinaV2.avisosAparato.hoja.aparato', { aparato: nombreAparato })}</h3>
        <div className="flex min-h-11 items-center gap-3 rounded-o-m bg-oficina-control/60 py-1.5 pl-3.5 pr-1.5" data-testid="hoja-aparato" data-estado={estado}>
          {estado === 'nada' ? null : (
            <>
              <Icono className={cn('h-[18px] w-[18px] shrink-0', estado === 'activos' || estado === 'activados' ? 'text-oficina-trabajando' : 'text-oficina-texto2')} aria-hidden />
              <p className="min-w-0 flex-1 py-1 text-o-texto text-oficina-texto" data-testid="hoja-aparato-texto">
                {estado === 'pedir' || estado === 'activando' ? t('oficinaV2.avisosAparato.hoja.pedir', { aparato: nombreAparato }) : textoDe(estado, aparato)}
              </p>
              {conAccion ? (
                <Boton variante="secundario" tamano="sm" cargando={estado === 'activando'} onClick={alActivar} data-testid="hoja-activar">
                  {t('oficinaV2.avisosAparato.activar')}
                </Boton>
              ) : null}
            </>
          )}
        </div>
        <p className="mt-2 text-o-sub text-oficina-texto3" data-testid="hoja-sistema">
          {t(`oficinaV2.avisosAparato.hoja.sistema.${sistema}`)}
        </p>
      </section>
    </div>
  );
}
